import React, { useMemo, useState, type JSX, type ReactNode } from "react";

export type AdminDataTableSortDirection = "asc" | "desc";

export type AdminDataTableSortState = {
  columnId: string;
  direction: AdminDataTableSortDirection;
};

type AdminDataTableSortValue = string | number | boolean | null | undefined;

export type AdminDataTableColumn<TRow> = {
  align?: "left" | "center" | "right";
  defaultSortDirection?: AdminDataTableSortDirection;
  id: string;
  label: string;
  renderCell: (row: TRow) => ReactNode;
  sortValue?: (row: TRow) => AdminDataTableSortValue;
  width?: string;
};

export type AdminDataTableProps<TRow> = {
  columns: AdminDataTableColumn<TRow>[];
  emptyState?: ReactNode;
  getRowId: (row: TRow) => string;
  initialSortState?: AdminDataTableSortState;
  onRowClick?: (row: TRow) => void;
  rows: TRow[];
};

function resolveTextAlign(align: AdminDataTableColumn<unknown>["align"]): "left" | "center" | "right" {
  return align ?? "left";
}

function isSortableColumn<TRow>(column: AdminDataTableColumn<TRow>): boolean {
  return typeof column.sortValue === "function";
}

function normalizeAdminDataTableSortValue(value: AdminDataTableSortValue): string | number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  if (typeof value === "string") {
    return value.toLocaleLowerCase();
  }

  return value;
}

function compareAdminDataTableSortValues(
  left: AdminDataTableSortValue,
  right: AdminDataTableSortValue,
): number {
  const normalizedLeft = normalizeAdminDataTableSortValue(left);
  const normalizedRight = normalizeAdminDataTableSortValue(right);

  if (normalizedLeft === undefined && normalizedRight === undefined) {
    return 0;
  }

  if (normalizedLeft === undefined) {
    return 1;
  }

  if (normalizedRight === undefined) {
    return -1;
  }

  if (typeof normalizedLeft === "number" && typeof normalizedRight === "number") {
    return normalizedLeft - normalizedRight;
  }

  return String(normalizedLeft).localeCompare(String(normalizedRight));
}

export function resolveNextAdminDataTableSortState<TRow>(
  currentSortState: AdminDataTableSortState | undefined,
  column: AdminDataTableColumn<TRow>,
): AdminDataTableSortState | undefined {
  if (!isSortableColumn(column)) {
    return currentSortState;
  }

  if (!currentSortState || currentSortState.columnId !== column.id) {
    return {
      columnId: column.id,
      direction: column.defaultSortDirection ?? "asc",
    };
  }

  return {
    columnId: column.id,
    direction: currentSortState.direction === "asc" ? "desc" : "asc",
  };
}

export function sortAdminDataTableRows<TRow>(
  rows: TRow[],
  columns: AdminDataTableColumn<TRow>[],
  sortState: AdminDataTableSortState | undefined,
): TRow[] {
  if (!sortState) {
    return rows;
  }

  const column = columns.find((candidate) => candidate.id === sortState.columnId);
  if (!column || !isSortableColumn(column)) {
    return rows;
  }

  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const compared = compareAdminDataTableSortValues(
        column.sortValue?.(left.row),
        column.sortValue?.(right.row),
      );

      if (compared !== 0) {
        return sortState.direction === "asc" ? compared : -compared;
      }

      return left.index - right.index;
    })
    .map(({ row }) => row);
}

function toAriaSort(
  sortable: boolean,
  activeSortState: AdminDataTableSortState | undefined,
  columnId: string,
): "ascending" | "descending" | "none" | undefined {
  if (!sortable) {
    return undefined;
  }

  if (!activeSortState || activeSortState.columnId !== columnId) {
    return "none";
  }

  return activeSortState.direction === "asc" ? "ascending" : "descending";
}

export function AdminDataTable<TRow>(props: AdminDataTableProps<TRow>): JSX.Element {
  const [sortState, setSortState] = useState<AdminDataTableSortState | undefined>(
    props.initialSortState,
  );
  const sortedRows = useMemo(
    () => sortAdminDataTableRows(props.rows, props.columns, sortState),
    [props.rows, props.columns, sortState],
  );

  if (props.rows.length === 0 && props.emptyState) {
    return <>{props.emptyState}</>;
  }

  return (
    <div
      style={{
        overflowX: "auto",
        borderRadius: "24px",
        border: "1px solid rgba(24, 33, 43, 0.08)",
        background: "rgba(255, 255, 255, 0.82)",
        boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          minWidth: "720px",
        }}
      >
        <thead>
          <tr
            style={{
              background: "rgba(248, 250, 252, 0.92)",
            }}
          >
            {props.columns.map((column) => (
              <th
                key={column.id}
                aria-sort={toAriaSort(isSortableColumn(column), sortState, column.id)}
                scope="col"
                style={{
                  padding: "12px 14px",
                  textAlign: resolveTextAlign(column.align),
                  color: "#64748b",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderBottom: "1px solid rgba(24, 33, 43, 0.08)",
                  width: column.width,
                }}
              >
                {isSortableColumn(column) ? (
                  <button
                    onClick={() => setSortState((current) => resolveNextAdminDataTableSortState(current, column))}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      color: "inherit",
                      font: "inherit",
                      letterSpacing: "inherit",
                      textTransform: "inherit",
                      cursor: "pointer",
                    }}
                    type="button"
                  >
                    <span>{column.label}</span>
                    {sortState?.columnId === column.id ? (
                      <span style={{ fontSize: "0.72rem" }}>
                        {sortState.direction === "asc" ? "ASC" : "DESC"}
                      </span>
                    ) : null}
                  </button>
                ) : (
                  column.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={props.getRowId(row)}
              onClick={props.onRowClick ? () => props.onRowClick?.(row) : undefined}
              style={{
                cursor: props.onRowClick ? "pointer" : "default",
              }}
            >
              {props.columns.map((column) => (
                <td
                  key={column.id}
                  style={{
                    padding: "12px 14px",
                    textAlign: resolveTextAlign(column.align),
                    borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                    color: "#18212b",
                    fontSize: "0.9rem",
                    verticalAlign: "top",
                  }}
                >
                  {column.renderCell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
